import type { EFinancingCompany } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { ActionButton, ActionLink, BodyText, FieldValue, Icon } from '@if/mui'
import { Box, Card, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import type { BankJobDto } from '@src/api/lender-payment/bank-jobs-api'
import { useCompany } from '@src/services/company-hooks'
import { EPaymentMethod } from '@src/types/constants'
import { FileSpreadsheet, FileText, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const COLUMN_COUNT = 6

/** A file's detail page, which lists the offices it paid. */
const bankJobPath = (company: EFinancingCompany, bankJobId: number) => `/${company}/disbursements/history/${bankJobId}`

// The flag on a deposit with lines in failure, cancelled or sent back by the bank: every line in
// red, only some in yellow.
const failureFlag = (file: BankJobDto) => {
  if (file.isFullyFailed) return { message: 'disburse.history.fullyFailed', color: 'error' } as const
  if (file.isPartiallyFailed) return { message: 'disburse.history.partiallyFailed', color: 'warning' } as const
  return null
}

interface Props {
  files: BankJobDto[]
}

// One row per deposit, newest first. Its number leads to the deposit's detail page; an ACP file's
// row also carries the file's two downloads, which a cheque or a direct deposit does not have.
const BankJobTable = ({ files }: Props) => {
  const { t } = useTranslation()
  const company = useCompany()

  // Sent by pre-authorized debit, a deposit reaches its payees as a bank transfer: the history says
  // so, the enum's "pre-authorized payment" being the customer-payment wording.
  const methodLabel = (method: EPaymentMethod) =>
    method === EPaymentMethod.PreAuthorizedDebit
      ? t('disburse.history.bankTransfer')
      : t(`enum.EPaymentMethod.${method}`)

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor: theme.vars.palette.tableHead.bg,
                '& .MuiTableCell-root': { color: theme.vars.palette.tableHead.fg },
              })}
            >
              <TableCell>{t('disburse.history.columns.acpFile')}</TableCell>
              <TableCell>{t('disburse.history.paymentColumns.type')}</TableCell>
              <TableCell>{t('disburse.history.columns.date')}</TableCell>
              <TableCell>{t('disburse.history.columns.createdBy')}</TableCell>
              <TableCell align="right">{t('disburse.history.columns.amount')}</TableCell>
              <TableCell align="right">{t('disburse.history.columns.download')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => {
              const failure = failureFlag(file)

              return (
                <TableRow key={file.bankJobId}>
                  {/* TODO: the "Retour reçu" chip joins the number once the list carries returns. */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ActionLink href={bankJobPath(company, file.bankJobId)}>#{file.fileNumber}</ActionLink>
                      {failure && (
                        // The span carries the tooltip's ref, which the icon itself does not.
                        <Tooltip title={t(failure.message)}>
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            <Icon icon={TriangleAlert} size="xs" color={failure.color} />
                          </Box>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <FieldValue>{methodLabel(file.bankOperationMethod)}</FieldValue>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <FieldValue>{file.depositOn}</FieldValue>
                  </TableCell>
                  <TableCell>
                    <FieldValue>{file.createdByFullName}</FieldValue>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <FieldValue>{formatCurrency(file.amount)}</FieldValue>
                  </TableCell>
                  <TableCell align="right">
                    {/* TODO: wire both downloads once their data step lands. */}
                    {file.bankOperationMethod === EPaymentMethod.PreAuthorizedDebit && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title={t('disburse.history.downloadAcp')}>
                          <ActionButton size="small" startIcon={FileText}>
                            {t('disburse.history.acpButton')}
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title={t('disburse.history.downloadCsv')}>
                          <ActionButton size="small" startIcon={FileSpreadsheet}>
                            {t('disburse.history.csvButton')}
                          </ActionButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {files.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <BodyText color="text.secondary">{t('disburse.history.empty')}</BodyText>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Card>
  )
}

export default BankJobTable
